import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import SoftAvatar from "components/SoftAvatar";
import userImage from "assets/images/illustrations/user.png";
import SoftButton from "components/SoftButton";
import { ApiResponse } from "model/apiResponseModel";
import api from "api/api";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ConfirmDialog from "./components/ConfirmDialog";
import { IoIosInformationCircle } from "react-icons/io";
import Description from "./components/Description";

function Leaderboard() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [leaderboardType, setLeaderboardType] = useState("current"); // 'current' or 'all_time'
  const [isCompetitionActive, setIsCompetitionActive] = useState(false);
  const [competitionStatus, setCompetitionStatus] = useState("");
  const [competitionStartDate, setCompetitionStartDate] = useState("");
  const [showPreviousLeaderboards, setShowPreviousLeaderboards] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    isLoading: false,
    type: null,
  });
  const [currentView, setCurrentView] = useState("current"); // 'current', 'all_time', or 'previous'
  const [descriptionDialog, setDescriptionDialog] = useState({
    isOpen: false,
    isLoading: false,
  });
  const [currentDescription, setCurrentDescription] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedTier, setSelectedTier] = useState("Bronze");
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);
  const tiers = ["Bronze", "Silver", "Gold", "Diamond"];

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setSelectedCompetitionId(null);
      setLeaderboardType(newType);
      setCurrentView(newType);
    }
  };

  const fetchPreviousLeaderboardData = async (competition_id, tier) => {
    try {
      setIsLoading(true);
      const response = await api.getPreviousLeaderboardData({
        competition_id,
        tier: tier?.toLowerCase(),
      });
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setColumns([
          { name: "position", align: "left" },
          { name: "user", align: "left" },
          { name: "points", align: "center" },
          { name: "projects", align: "center" },
        ]);

        setRows(
          response.data.participants.map((item, index) => ({
            position: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium" pl={4}>
                {index + 1}
              </SoftTypography>
            ),
            user: (
              <SoftBox
                display="flex"
                alignItems="center"
                pr={1}
                pb={0.5}
                sx={{ cursor: "pointer" }}
              >
                <SoftAvatar
                  src={item.profile_picture || userImage}
                  alt={item.name}
                  size="sm"
                  variant="rounded"
                />
                <SoftTypography variant="button" fontWeight="medium" pl={1}>
                  {item.name}
                </SoftTypography>
              </SoftBox>
            ),
            points: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.total_points}
              </SoftTypography>
            ),
            projects: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.numberOfProjects}
              </SoftTypography>
            ),
          }))
        );
      } else {
        setColumns([]);
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching previous leaderboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompetitionRowClick = (competition_id) => {
    setSelectedCompetitionId(competition_id);
  };

  const fetchPreviousLeaderboards = async () => {
    try {
      setIsLoading(true);
      setSelectedCompetitionId(null);
      setCurrentView("previous");
      const response = await api.getPreviousLeaderboard();
      const apiResponse = new ApiResponse(response.status, response.message);
      if (apiResponse.status === 1) {
        setColumns([
          { name: "index", align: "left" },
          { name: "start date", align: "center" },
          { name: "end date", align: "center" },
          { name: "participants", align: "center" },
        ]);

        setRows(
          response.data.competitions.map((item, index) => ({
            index: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium" pl={3}>
                {index + 1}
              </SoftTypography>
            ),
            "start date": (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {new Date(item.created_at).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </SoftTypography>
            ),
            "end date": (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {new Date(item.ended_at).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </SoftTypography>
            ),
            participants: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.participantCount}
              </SoftTypography>
            ),
            clickEvent: () => handleCompetitionRowClick(item._id),
            style: { cursor: 'pointer' }
          }))
        );
      } else {
        setColumns([]);
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching previous leaderboards:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeaderboard = async (type, tier) => {
    try {
      setIsLoading(true);
      const response = await api.getLeaderboard({ type, tier: tier?.toLowerCase() });
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        if (response.data.status) {
          setCompetitionStatus(response.data.status);
        }
        if (response.data.description !== "") {
          setCurrentDescription(response.data.description || "");
        }
        if (response.data.created_at) {
          setCompetitionStartDate(new Date(response.data.created_at).toLocaleDateString("en-GB"));
        }
        if (response.data.status !== "ongoing" && type === "current") {
          setColumns([]);
          setRows([]);
          setIsCompetitionActive(false);
          return;
        }

        setColumns([
          { name: "position", align: "left" },
          { name: "user", align: "left" },
          { name: "points", align: "center" },
          { name: "projects", align: "center" },
        ]);

        setRows(
          response.data.participants.map((item, index) => ({
            position: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium" pl={4}>
                {index + 1}
              </SoftTypography>
            ),
            user: (
              <SoftBox
                display="flex"
                alignItems="center"
                pr={1}
                pb={0.5}
                onClick={() => navigate(`/user-detail?userId=${item.userId}`)}
                sx={{ cursor: "pointer" }}
              >
                <SoftAvatar
                  src={item.profile_picture || userImage}
                  alt={item.name}
                  size="sm"
                  variant="rounded"
                />
                <SoftTypography variant="button" fontWeight="medium" pl={1}>
                  {item.name}
                </SoftTypography>
              </SoftBox>
            ),
            points: (
              <SoftTypography
                variant="caption"
                color="secondary"
                fontWeight="medium"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "40px",
                }}
              >
                {item.total_points}
              </SoftTypography>
            ),
            projects: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {item.numberOfProjects}
              </SoftTypography>
            ),
          }))
        );

        setIsCompetitionActive(response.data.status === "ongoing");
      } else {
        setColumns([]);
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      setColumns([]);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompetitionClick = (type) => {
    setConfirmDialog({
      isOpen: true,
      isLoading: false,
      type,
    });
  };

  const handleConfirmDialog = async () => {
    try {
      setConfirmDialog((prev) => ({ ...prev, isLoading: true }));

      let response;
      if (confirmDialog.type === "end") {
        response = await api.endCompetition();
        setCompetitionStatus(response.data.status);
        setCurrentDescription("");
      } else {
        response = await api.createCompetition("start");
        setCompetitionStatus(response.data.status);
        setCurrentDescription("");
      }

      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setIsCompetitionActive(!isCompetitionActive);
        if (!isCompetitionActive) {
          setLeaderboardType("current");
        }
        fetchLeaderboard(leaderboardType);
      }
    } catch (error) {
      console.error("Error managing competition:", error);
    } finally {
      setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleInfoClick = () => {
    setDescriptionDialog({ isOpen: true, isLoading: false });
  };

  const handleSaveDescription = async (description) => {
    try {
      setDescriptionDialog(prev => ({ ...prev, isLoading: true }));
      const response = await api.editLeaderboardDescription({ description });
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setDescriptionDialog({ isOpen: false, isLoading: false });
      }
    } catch (error) {
      console.error("Error saving description:", error);
    } finally {
      setDescriptionDialog(prev => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    if (selectedCompetitionId) {
      fetchPreviousLeaderboardData(selectedCompetitionId, selectedTier);
    } else if (currentView !== "previous") {
      fetchLeaderboard(leaderboardType, selectedTier);
    }
  }, [leaderboardType, selectedTier, selectedCompetitionId, currentView]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={2}>
              <SoftTypography variant="h6">
                {currentView === "previous" ? "Previous Leaderboard" : "Leaderboard"}
              </SoftTypography>
              <SoftBox display="flex" gap={2}>
                <SoftTypography
                  variant="caption"
                  color="secondary"
                  fontWeight="medium"
                  alignSelf="center"
                >
                  {competitionStatus === "ongoing" &&
                    currentView === "current" &&
                    <IoIosInformationCircle
                      size={24}
                      style={{ marginTop: '5px', cursor: 'pointer' }}
                      onClick={handleInfoClick}
                    />}
                </SoftTypography>
                {currentView === "current" && (
                  <SoftButton
                    variant="gradient"
                    color={isCompetitionActive ? "error" : "success"}
                    onClick={() => handleCompetitionClick(isCompetitionActive ? "end" : "start")}
                  >
                    {isCompetitionActive ? "End Competition" : "Start Competition"}
                  </SoftButton>
                )}
                {/* <LeaderboardFilter status={filterStatus} setStatus={setFilterStatus} /> */}
                <SoftBox display="flex" gap={2} alignItems="center" flexDirection="column">
                  <ToggleButtonGroup
                    value={currentView}
                    exclusive
                    onChange={handleTypeChange}
                    size="small"
                    aria-label="leaderboard type"
                  >
                    <ToggleButton value="all_time" aria-label="all time" sx={{ py: 1 }}>
                      All Time
                    </ToggleButton>
                    <ToggleButton value="current" aria-label="current" sx={{ py: 1 }}>
                      Current
                    </ToggleButton>
                  </ToggleButtonGroup>
                </SoftBox>
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" justifyContent="flex-end" alignItems="end" px={3} pb={2} gap={2}>

              {/* { currentView === "current" && */}

              <SoftBox display="flex" gap={2}>
                {tiers.map((tier) => (
                  <SoftBox
                    key={tier}
                    onClick={() => setSelectedTier(tier)}
                    sx={{
                      cursor: "pointer",
                      px: 2,
                      py: 0.5,
                      borderRadius: "50px",
                      border: "1px solid #4B319D",
                      backgroundColor: selectedTier === tier ? "#523a92" : "transparent",
                      color: selectedTier === tier ? "#FFFFFF" : "#523a92",
                      transition: "all 0.3s ease",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "100px",
                      "&:hover": {
                        backgroundColor: selectedTier === tier ? "#523a92" : "rgba(75, 49, 157, 0.1)",
                      },
                    }}
                  >
                    {tier}
                  </SoftBox>
                ))}
              </SoftBox>

              {/* } */}

              <SoftTypography
                variant="caption"
                color="secondary"
                fontWeight="medium"
                style={{ cursor: "pointer" }}
                alignSelf="center"
                onClick={fetchPreviousLeaderboards}
              >
                Previous Leaderboards
              </SoftTypography>
            </SoftBox>

            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {isLoading ? (
                <SoftBox
                  display="flex"
                  height="350px"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    Loading...
                  </SoftTypography>
                </SoftBox>
              ) : rows.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    No Data Found
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        handleClose={handleCloseDialog}
        handleConfirm={handleConfirmDialog}
        isLoading={confirmDialog.isLoading}
        title={
          confirmDialog.type === "end"
            ? "Are you sure you want to end the current competition?"
            : "Are you sure you want to start a new competition?"
        }
        actionText={confirmDialog.type === "end" ? "End Competition" : "Start Competition"}
      />
      <Description
        isOpen={descriptionDialog.isOpen}
        handleClose={() => setDescriptionDialog({ isOpen: false, isLoading: false })}
        handleSave={handleSaveDescription}
        isLoading={descriptionDialog.isLoading}
        currentDescription={currentDescription}
        startDate={competitionStartDate}
      />
    </DashboardLayout>
  );
}

export default Leaderboard;
